/**
 * UsersStatsPage - Admin panel listing all users with photo counts and storage usage.
 *
 * Calls the `listUsersWithStats` HTTPS callable which uses Firestore count()
 * aggregation to compute per-user stats cheaply.
 */

import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Space,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/config/firebase";
import { formatStorage } from "@/services/stripeService";
import { MaterialIcon } from "@/components/MaterialIcon";

const { Title, Text } = Typography;

interface UserStats {
  userId: string;
  email: string;
  name: string;
  planTier: string;
  totalPhotos: number;
  photosWithRaw: number; // -1 = unknown (query failed)
  totalStorageBytes: number;
}

interface ListUsersWithStatsResponse {
  users: UserStats[];
}

const functions = getFunctions(app, "europe-west1");
const listUsersWithStatsFn = httpsCallable<
  Record<string, never>,
  ListUsersWithStatsResponse
>(functions, "listUsersWithStats");

const planTagColors: Record<string, string> = {
  free: "default",
  start: "blue",
  pro: "gold",
};

export default function UsersStatsPage() {
  const [users, setUsers] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const result = await listUsersWithStatsFn({});
      setUsers(result.data.users);
    } catch (error) {
      console.error("Failed to load users stats:", error);
      message.error(
        "Failed to load users stats. Check admin permissions."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPhotosAll = users.reduce((s, u) => s + u.totalPhotos, 0);
  const totalStorageAll = users.reduce(
    (s, u) => s + u.totalStorageBytes,
    0
  );

  const columns: ColumnsType<UserStats> = [
    {
      title: "User",
      key: "user",
      render: (_, row) => (
        <div>
          <Text strong>{row.email || "(no email)"}</Text>
          {row.name && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.name}
              </Text>
            </div>
          )}
        </div>
      ),
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Plan",
      dataIndex: "planTier",
      key: "plan",
      width: 100,
      render: (tier: string) => (
        <Tag color={planTagColors[tier] || "default"}>
          {tier.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "Free", value: "free" },
        { text: "Start", value: "start" },
        { text: "Pro", value: "pro" },
      ],
      onFilter: (value, row) => row.planTier === value,
    },
    {
      title: "Photos",
      dataIndex: "totalPhotos",
      key: "totalPhotos",
      width: 120,
      align: "right",
      render: (count: number) => count.toLocaleString(),
      sorter: (a, b) => a.totalPhotos - b.totalPhotos,
      defaultSortOrder: "descend",
    },
    {
      title: "JPEG only",
      key: "jpegOnly",
      width: 120,
      align: "right",
      render: (_, row) => {
        if (row.photosWithRaw < 0) return <Text type="secondary">—</Text>;
        return (row.totalPhotos - row.photosWithRaw).toLocaleString();
      },
      sorter: (a, b) => {
        const aJ = a.photosWithRaw < 0 ? 0 : a.totalPhotos - a.photosWithRaw;
        const bJ = b.photosWithRaw < 0 ? 0 : b.totalPhotos - b.photosWithRaw;
        return aJ - bJ;
      },
    },
    {
      title: "JPEG + RAW",
      dataIndex: "photosWithRaw",
      key: "photosWithRaw",
      width: 120,
      align: "right",
      render: (count: number) => {
        if (count < 0) return <Text type="secondary">—</Text>;
        return count.toLocaleString();
      },
      sorter: (a, b) =>
        Math.max(0, a.photosWithRaw) - Math.max(0, b.photosWithRaw),
    },
    {
      title: "Storage",
      dataIndex: "totalStorageBytes",
      key: "storage",
      width: 120,
      align: "right",
      render: (bytes: number) => formatStorage(bytes),
      sorter: (a, b) => a.totalStorageBytes - b.totalStorageBytes,
    },
  ];

  return (
    <div>
      <Title level={3}>Users Stats</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Platform-wide overview: photo counts and storage usage per user.
      </Text>

      <Space style={{ marginBottom: 16 }} size="large" wrap>
        <span>
          <Text type="secondary">Users: </Text>
          <Text strong>{users.length}</Text>
        </span>
        <span>
          <Text type="secondary">Total photos: </Text>
          <Text strong>{totalPhotosAll.toLocaleString()}</Text>
        </span>
        <span>
          <Text type="secondary">Total storage: </Text>
          <Text strong>{formatStorage(totalStorageAll)}</Text>
        </span>
        <Button
          icon={<MaterialIcon name="refresh" />}
          onClick={load}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>

      <Card>
        {loading && users.length === 0 ? (
          <Skeleton active title={false} paragraph={{ rows: 8 }} />
        ) : (
          <Table
            columns={columns}
            dataSource={users}
            rowKey="userId"
            pagination={{ pageSize: 20, showSizeChanger: true }}
            size="middle"
          />
        )}
      </Card>
    </div>
  );
}
